import React, { useState, useEffect, useRef, useCallback } from "react";
import "../super-admin/customers/ControlPanel.css";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Button,
  Icon,
} from "../../components/Component";
import { Card } from "reactstrap";
import { Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Table, Empty, Select, Tag, Tooltip, Input, Drawer, Badge as AntBadge, Skeleton } from "antd";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import { SearchOutlined, ReloadOutlined, SettingOutlined, InfoCircleOutlined, FilterOutlined, TagOutlined, CreditCardOutlined } from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import mastersServices from "../../api/masters/mastersService";
import superAdminService from "../../api/super-admin/superAdminService";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const ListOfParties: React.FC = () => {
  const navigate = useNavigate();
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [settingUpOrgId, setSettingUpOrgId] = useState<number | null>(null);

  // Filter state
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [filterBillingStatus, setFilterBillingStatus] = useState<string | undefined>(undefined);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterType) filters.type = filterType;
    if (filterBillingStatus) filters.billingStatus = filterBillingStatus;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterType, filterBillingStatus]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const filters = params.filters !== undefined ? params.filters : buildFilters();
      const apiParams: any = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff ?? pagination.pageDiff,
        paginationId: params.paginationId ?? pagination.paginationId,
        searchQuery:
          params.searchQuery !== undefined
            ? params.searchQuery
            : onSearchText,
      };

      if (filters) {
        if (filters.type) apiParams["filters[type]"] = filters.type;
        if (filters.billingStatus) apiParams["filters[billingStatus]"] = filters.billingStatus;
      }

      const res = await mastersServices.listofpartiesWithPagination(apiParams);

      if (res.status) {
        const data = res.data;
        const list = Array.isArray(data) ? data : data?.list || [];
        const count = Array.isArray(data) ? data.length : data?.count || 0;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && {
            paginationId: params.paginationId,
          }),
        }));
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      console.error("Failed to fetch parties:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
      searchQuery: debouncedSearch.trim(),
    });
  }, [debouncedSearch]);

  const tableOnChange = async (
    newPagination: any,
    _filters: any,
    _sorter: any
  ) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };

    if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(
        ((pagination.current - 1) * pagination.pageSize) /
        newPagination.pageSize
      );
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction =
        newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff =
        Math.abs(newPagination.current - pagination.current) - 1;

      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[dataSource.length - 1]?.orgID;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.orgID;
      }
    }

    await fetchData(apiParams);
  };

  const handleClearFilters = () => {
    setFilterType(undefined);
    setFilterBillingStatus(undefined);
  };

  const filtersInitialized = useRef(false);

  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, [filterType, filterBillingStatus]);

  const handleSetupParty = async (orgId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: "Setup Control Panel?",
      text: "This will create a party record and billing configuration for this organization.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Setup",
      confirmButtonColor: "#6576ff",
    });

    if (!result.isConfirmed) return;

    setSettingUpOrgId(orgId);
    try {
      console.log("[ListOfParties] Setting up party for orgId:", orgId);
      const res = await superAdminService.setupPartyForOrg(orgId);
      console.log("[ListOfParties] Setup response:", res);
      if (res.status) {
        Swal.fire("Success", res.message || "Control panel setup successfully", "success");
        fetchData({
          current: pagination.current,
          pageDiff: 0,
          paginationId: null,
          direction: "P",
        });
      } else {
        console.warn("[ListOfParties] Setup failed:", res.message);
        Swal.fire("Error", res.message || "Failed to setup", "error");
      }
    } catch (error: any) {
      console.error("[ListOfParties] Setup error:", error);
      Swal.fire("Error", error.message || "Failed to setup", "error");
    } finally {
      setSettingUpOrgId(null);
    }
  };

  const orgTypeTag = (type: string) => {
    const isBuyer = type === "Buyer";
    return (
      <Tag
        color={isBuyer ? "blue" : "purple"}
        style={{ fontSize: 11, fontWeight: 600, borderRadius: 4, padding: "0 6px", lineHeight: "18px" }}
      >
        {type}
      </Tag>
    );
  };

  const billingStatusTag = (billingStatus: string) => {
    if (!billingStatus) return <span style={{ color: "#8091a7" }}>-</span>;
    const map: Record<string, { color: string; label: string }> = {
      Active: { color: "green", label: "Active" },
      Hold: { color: "red", label: "On Hold" },
      Draft: { color: "default", label: "Draft" },
    };
    const cfg = map[billingStatus] || { color: "default", label: billingStatus };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  };

  const ellipsisCell = (val: string, style?: React.CSSProperties) =>
    val ? (
      <Tooltip title={val} placement="topLeft">
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", ...style }}>
          {val}
        </span>
      </Tooltip>
    ) : (
      <span style={{ color: "#b0b0b0" }}>-</span>
    );

  const columns = [
    {
      title: "Organisation",
      dataIndex: "name",
      key: "name",
      width: 280,
      fixed: "left" as const,
      render: (text: string, record: any) => (
        <Tooltip title={text} placement="topLeft">
          <a
            href="#view"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/party-details/${record.orgID}`);
            }}
            style={{ display: "flex", alignItems: "center", gap: 6, textDecoration: "none", overflow: "hidden" }}
          >
            <span style={{ flexShrink: 0 }}>{orgTypeTag(record?.type)}</span>
            <span style={{ fontWeight: 600, color: "#364a63", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {text || "-"}
            </span>
          </a>
        </Tooltip>
      ),
    },
    {
      title: "GSTIN",
      dataIndex: "gstin",
      key: "gstin",
      width: 155,
      render: (val: string) => (
        <span style={{ fontFamily: "monospace", fontSize: 12, color: "#526484" }}>{val || "-"}</span>
      ),
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
      width: 150,
      render: (val: string) => ellipsisCell(val),
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 120,
      render: (val: string) => val || <span style={{ color: "#b0b0b0" }}>-</span>,
    },
    {
      title: "Email",
      dataIndex: "adminEmail",
      key: "adminEmail",
      width: 210,
      render: (val: string) => ellipsisCell(val, { fontSize: 12 }),
    },
    {
      title: "State",
      key: "state",
      width: 130,
      render: (_: any, record: any) => {
        const val = record?.companyAddress?.state?.stateName;
        return val ? ellipsisCell(val) : <span style={{ color: "#b0b0b0" }}>-</span>;
      },
    },
    {
      title: "City",
      key: "city",
      width: 110,
      render: (_: any, record: any) => {
        const val = record?.companyAddress?.city;
        return val ? ellipsisCell(val) : <span style={{ color: "#b0b0b0" }}>-</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status: string) => (
        <Tag color={status === "Active" ? "green" : "red"} style={{ fontWeight: 500 }}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Billing Status",
      dataIndex: "billingStatus",
      key: "billingStatus",
      width: 110,
      render: (val: string) => billingStatusTag(val),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 120,
      render: (date: string) => (
        <span style={{ color: "#526484", fontSize: 12 }}>
          {date ? moment(date).format("DD MMM YYYY") : "-"}
        </span>
      ),
    },
    {
      title: "",
      key: "controlPanel",
      width: 90,
      fixed: "right" as const,
      render: (_: any, record: any) =>
        record.partyId ? (
          <Tooltip title="Control Panel">
            <a
              href="#control-panel"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/party-details/${record.orgID}`, {
                  state: { activeTab: "control-panel" },
                });
              }}
              style={{ fontSize: 16, color: "#6576ff" }}
            >
              <SettingOutlined />
            </a>
          </Tooltip>
        ) : (
          <a
            href="#setup"
            onClick={(e) => {
              e.preventDefault();
              handleSetupParty(record.orgID, e);
            }}
            className="btn btn-sm btn-primary btn-dim"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {settingUpOrgId === record.orgID ? (
              "Setting up..."
            ) : (
              <>
                <em className="icon ni ni-setting" style={{ marginRight: 4 }}></em>
                Setup
              </>
            )}
          </a>
        ),
    },
  ];

  const hasActiveFilters = filterType || filterBillingStatus;
  const activeFilterCount = [filterType, filterBillingStatus].filter(Boolean).length;

  return (
    <React.Fragment>
      <Head title="List of parties" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                List of Parties
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} parties.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                className="btn-dim"
                onClick={() => navigate("/party-onboarding")}
              >
                <Icon name="plus"></Icon>
                <span>Add New Party</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner py-3">
              <div className="d-flex justify-content-between align-items-center">
                <Input
                  prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                  placeholder="Search by name, GSTIN, email, contact, phone..."
                  value={onSearchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="borderless"
                  style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                  allowClear
                />
                <div className="d-flex align-items-center" style={{ gap: 4 }}>
                  <Tooltip title="Refresh">
                    <span
                      onClick={() => {
                        hasFetched.current = false;
                        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                      }}
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <ReloadOutlined style={{ fontSize: 16, color: "#555" }} />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                      <span
                        onClick={() => setFilterDrawerOpen(true)}
                        style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <FilterOutlined style={{ fontSize: 16, color: "#555" }} />
                      </span>
                    </AntBadge>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="card-inner p-0">
              {loading ? (
                <table className="skeleton-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                      {["Organisation", "Registered As", "GSTIN", "Contact Name", "Phone", "Email", "State", "City", "Status", "Created Date", ""].map((h) => (
                        <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 130, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 52, height: 22, borderRadius: 11 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 140, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 110, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 90, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 150, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 50, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 30, height: 18, borderRadius: 4 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : dataSource.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="orgID"
                  rowClassName={(record: any) => record.billingStatus === "Hold" ? "receivable-row-hold" : ""}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                  }}
                  onChange={tableOnChange}
                  scroll={{ x: 1500, y: undefined }}
                  bordered
                  size="small"
                  onRow={(record) => ({
                    onClick: () => navigate(`/party-details/${record.orgID}`),
                    style: { cursor: "pointer" },
                  })}
                />
              ) : (
                <Empty description="No parties found" style={{ padding: "40px 0" }} />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      {/* Filter Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>
            {activeFilterCount > 0 && (
              <AntBadge
                count={activeFilterCount}
                style={{ backgroundColor: "#6576ff", marginLeft: 4 }}
              />
            )}
          </div>
        }
        placement="right"
        onClose={() => setFilterDrawerOpen(false)}
        open={filterDrawerOpen}
        width={450}
        zIndex={1060}
        extra={
          hasActiveFilters ? (
            <span
              style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }}
              onClick={handleClearFilters}
            >
              Reset All
            </span>
          ) : null
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Registered As */}
          <div style={{
            padding: "14px 16px", borderRadius: 10,
            background: "linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)",
            border: "1px solid #d9f7be",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <TagOutlined style={{ color: "#52c41a", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Registered As</span>
            </div>
            <Select
              placeholder="All types"
              allowClear
              style={{ width: "100%" }}
              value={filterType}
              onChange={(val) => setFilterType(val)}
              options={[
                { label: "Buyer", value: "Buyer" },
                { label: "Seller", value: "Seller" },
              ]}
            />
          </div>

          {/* Billing Status */}
          <div style={{
            padding: "14px 16px", borderRadius: 10,
            background: "linear-gradient(135deg, #fff7e6 0%, #fff1f0 100%)",
            border: "1px solid #ffd591",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <CreditCardOutlined style={{ color: "#fa8c16", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Billing Status</span>
            </div>
            <Select
              placeholder="All billing statuses"
              allowClear
              style={{ width: "100%" }}
              value={filterBillingStatus}
              onChange={(val) => setFilterBillingStatus(val)}
              options={[
                { label: "Active", value: "Active" },
                { label: "On Hold", value: "Hold" },
                { label: "Draft", value: "Draft" },
              ]}
            />
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default ListOfParties;
